import React from 'react';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import {
    FormattedDate,
    FormattedNumber,
    FormattedMessage,
    useIntl,
    defineMessage,
} from 'react-intl';

import { 
    Config,
    FormattedStreetAddress,
    formatPhoneNumber,
} from '@ratehub/base-ui';
import {
    COMMON_MESSAGES,
    ConfirmationPageMessages,
    ConfirmationPageTemplate,
    LifeInsuranceTypeMessages,
    ProviderPropType,
} from '@ratehub/ins-common';

import {
    InsLifeWizardFormTypes,
    InsLifeWizardInsuranceTypes,
} from '../definitions/CurrentDocument';
import formatDisabilityCoverageAmount from '../functions/formatDisabilityCoverageAmount';
import { CoverageAmountMessages } from '../definitions/CoverageAmountOptions';
import PROVIDER_IDS from '../definitions/ProviderIds';


function ConfirmationPage({
    hasJointApplicant = false,
    values,
}) {
    const intl = useIntl();

    const {
        firstName,
        formType,
        provider,
    } = values;

    const details = [
        getCoverageDetails(values, formType),
        getCustomerInformation(values, intl),
    ];

    if (hasJointApplicant) {
        details.push(getJointApplicantInformation(values));
    }

    if (formType === InsLifeWizardFormTypes.GROUP) {
        details.push(getCompanyInformation(values));
    }

    const isPrimaryProvider = provider.id === PROVIDER_IDS.PRIMARY_PROVIDER;

    return (
        <ConfirmationPageTemplate
            title={(
                <FormattedMessage
                    id="InsLifeWizard.ConfirmationPage.Title"
                    defaultMessage="{name}, your next step is a quick call with a {provider} broker"
                    values={{
                        name: firstName,
                        provider: provider.name,
                    }}
                />
            )}
            subtitle={(
                <FormattedMessage
                    id="InsLifeWizard.ConfirmationPage.SubTitle"
                    defaultMessage="Your licensed broker will help you compare quotes from Canada’s top life insurance companies, so you can get the right coverage at the best price."
                    values={{
                        partner: provider.name,
                        phone: (
                            <a href={`tel:${provider.phoneNumber}`}>
                                {formatPhoneNumber(provider.phoneNumber)}
                            </a>),
                    }}
                />
            )}
            
            hideContactInformation
            hideDisclaimer

            provider={provider}

            customCTA={
                // ! Requirement: We want to hide the custom CTA for CIS only
                !isPrimaryProvider
                && {
                    href: Config.CANWISE_LIFE_INSURANCE_URL,
                    message: (
                        <FormattedMessage
                            id="InsLifeWizard.ConfirmationPage.ViewCanWiseWebsite"
                            defaultMessage="Visit website"
                        />
                    ),
                    dataName: 'visitWebsiteCTA',
                }
            }

            details={details}
        />
    );
}

ConfirmationPage.propTypes = {
    hasJointApplicant: PropTypes.bool,

    values: PropTypes.shape({
        formType: PropTypes.oneOf(Object.values(InsLifeWizardFormTypes)),
        insuranceType: PropTypes.oneOf(Object.values(InsLifeWizardInsuranceTypes)),
        provider: ProviderPropType,

        firstName: PropTypes.string,
        fullName: PropTypes.string,
        dateOfBirth: PropTypes.string,
        hasConsumedTobacco: PropTypes.bool,
        phone: PropTypes.string,
        email: PropTypes.string,

        jointApplicantFullName: PropTypes.string,
        jointApplicantDateOfBirth: PropTypes.string,
        hasJointApplicantConsumedTobacco: PropTypes.bool,
    }).isRequired,
};

const IS_SMOKER_MESSAGE = defineMessage({
    id: 'InsLifeWizard.ConfirmationPage.isSmokerLabel',
    defaultMessage: 'Consumed tobacco in the last year',
});

function getCoverageDetails(values, formType) {
    const items = [
        {
            label: (
                <FormattedMessage
                    id="InsLifeWizard.ConfirmationPage.CoverageType"
                    defaultMessage="Type"
                />
            ),
            value: LifeInsuranceTypeMessages[values.insuranceType],
        },
    ];

    // If we should display the coverage amount
    if (formType !== InsLifeWizardFormTypes.GROUP) {
        let coverageAmount;

        // The Disability form has a different getter / set of values format
        if (formType === InsLifeWizardFormTypes.DISABILITY) {
            // Returns a <FormattedMessage>
            coverageAmount = formatDisabilityCoverageAmount(values.disabilityCoverageAmount);
        } else {
            coverageAmount = CoverageAmountMessages[values.coverageAmount];
        }

        items.push({
            label: (
                <FormattedMessage
                    id="InsLifeWizard.ConfirmationPage.CoverageAmount"
                    defaultMessage="Amount"
                />
            ),
            value: coverageAmount,
        });
    }

    return {
        title: ConfirmationPageMessages.COVERAGE_INFO_HEADING,
        items,
    };
}

function getCustomerInformation(values) {
    return {
        title: (
            <FormattedMessage
                id="InsLifeWizard.ConfirmationPage.CustomerInfo"
                defaultMessage="Customer information"
            />
        ),
        items: [
            {
                label: COMMON_MESSAGES.NAME,
                value: values.fullName,
            },
            {
                label: COMMON_MESSAGES.DATE_OF_BIRTH,
                value: (
                    <FormattedDate
                        value={values.dateOfBirth}
                        year="numeric"
                        month="long"
                        day="2-digit"
                    />
                ),
                isDate: true,
            },
            {
                label: IS_SMOKER_MESSAGE,
                value: values.hasConsumedTobacco
                    ? COMMON_MESSAGES.YES
                    : COMMON_MESSAGES.NO
                ,
            },
            {
                label: COMMON_MESSAGES.PHONE,
                value: formatPhoneNumber(values.phone),
            },
            {
                label: COMMON_MESSAGES.EMAIL,
                value: values.email,
            },
        ],
    };
}

function getJointApplicantInformation(values) {
    return {
        title: (
            <FormattedMessage
                id="InsLifeWizard.ConfirmationPage.JointApplicationInfo"
                defaultMessage="Joint applicant information"
            />
        ),
        items: [
            {
                label: COMMON_MESSAGES.NAME,
                value: values.jointApplicantFullName,
            },
            {
                label: COMMON_MESSAGES.DATE_OF_BIRTH,
                value: (
                    <FormattedDate
                        value={values.jointApplicantDateOfBirth}
                        year="numeric"
                        month="long"
                        day="2-digit"
                    />
                ),
                isDate: true,
            },
            {
                label: IS_SMOKER_MESSAGE,
                value: values.hasJointApplicantConsumedTobacco
                    ? COMMON_MESSAGES.YES
                    : COMMON_MESSAGES.NO
                ,
            },
        ],
    };
}

function getCompanyInformation(values) {
    return {
        title: (
            <FormattedMessage
                id="InsLifeWizard.ConfirmationPage.CompanyInfo"
                defaultMessage="Company information"
            />
        ),
        items: [
            {
                label: (
                    <FormattedMessage
                        id="InsLifeWizard.ConfirmationPage.CompanyNameLabel"
                        defaultMessage="Company name"
                    />
                ),
                value: values.companyName,
            },
            {
                label: (
                    <FormattedMessage
                        id="InsLifeWizard.ConfirmationPage.BusinessAddressLabel"
                        defaultMessage="Business address"
                    />
                ),
                // This needs to be called as a function to return the <FormattedMessage> properly
                value: FormattedStreetAddress({ address: values.businessAddress }),
            },
            {
                label: (
                    <FormattedMessage
                        id="InsLifeWizard.ConfirmationPage.NumberOfEmployeesLabel"
                        defaultMessage="Number of employees"
                    />
                ),
                value: (
                    <FormattedNumber
                        value={values.numberOfEmployees}
                    />
                ),
            },
        ],
    };
}

export default observer(ConfirmationPage);